import { filter, values, groupBy, sumBy, flatten } from 'lodash'
import { BindAll } from 'lodash-decorators'
import moment from 'moment'

import { observable, computed, action } from 'mobx'

import { TYPE_VENDOR } from 'common/data-access/company_websocket'
import { OrderChannelMessage, subscribeOrderChannel } from 'common/data-access/order_websocket'
import { formatOrderMaterialUnitCost, resetCompanyMaterialId, makeOrderMaterial } from 'common/helpers/order'
import { calcExtCost } from 'common/helpers/order'
import { OrderHistoryResponse } from 'common/server/order_history'
import { OrderMaterial } from 'common/server/orders'
import { OrderHit } from 'common/server/orders'
import { WebSocketMessage } from 'common/server/server_types'
import AlgoliaBaseStore from 'common/stores/AlgoliaBaseStore'
import ListBaseStore from 'common/stores/ListBaseStore'

import {
  OrderSessionOwner,
  UpdateOrderSessionBody,
  UpdateOrderSessionResponse,
  release_session,
  update_session,
} from 'vendor/server/order_sessions'
import { facets, index, IndexOrderRecord } from 'vendor/server/orders'
import { search_key, show, update, Order, history, comment, OrderUpdateParams } from 'vendor/server/orders'

class OrderListStore extends ListBaseStore<IndexOrderRecord> {
  index = index
  getFacets = facets
}

@BindAll()
export default class OrderStore extends AlgoliaBaseStore<OrderHit> {
  @observable selectedOrder: Nullable<Order> = null
  @observable orderMaterials: { [deliveryId: string]: OrderMaterial[] } = {}

  @observable selectedOrderDirty = false
  @observable newComment = ''

  @observable orderSessionOwner: Nullable<OrderSessionOwner> = null
  @observable webSocketSubscriptions = new Map()

  listStore: OrderListStore
  getSearchKey = search_key

  constructor() {
    super()

    this.listStore = new OrderListStore()
  }

  orderHistory = observable.array<OrderHistoryResponse>([])

  getOrderMaterialsByDeliveryId(deliveryId: string) {
    return this.orderMaterials[deliveryId]
  }

  @action
  handleWebSocketOrderSessionMessage(currentUserId: string) {
    return (message: WebSocketMessage<OrderChannelMessage>) => {
      if (message.data?.order_released) {
        this.orderSessionOwner = null
      }
      if (message.data?.order_will_expire) {
        if (this.orderSessionOwner?.id === currentUserId) {
          this.orderSessionOwner = {
            ...this.orderSessionOwner,
            expired: true,
          }
        }
      }
      if (message.data?.order_blocked && message.data.user?.id !== currentUserId) {
        this.orderSessionOwner = message.data.user
      }
    }
  }

  orderSessionSubscribe(id: string, currentUserId: string) {
    const subscription = subscribeOrderChannel(this.handleWebSocketOrderSessionMessage(currentUserId), TYPE_VENDOR, id)
    this.webSocketSubscriptions.set(id, subscription)
    return subscription
  }

  orderSessionUnsubscribe(id: string) {
    const subscription = this.webSocketSubscriptions.get(id)
    if (subscription) {
      subscription.unsubscribe()
      this.webSocketSubscriptions.delete(id)
    }
  }

  @action
  updateOrderMaterialsByDeliveryId(deliveryId: string, newOrderMaterials = []) {
    this.orderMaterials[deliveryId] = newOrderMaterials
  }

  getPlainOrderMaterials() {
    const orderMaterialsMatrix = Object.keys(this.orderMaterials).map((key) => {
      return this.orderMaterials[key]
    }, [])

    return flatten(orderMaterialsMatrix)
  }

  get orders() {
    return this.hits
  }

  @computed get orderMaterialSummary(): OrderMaterial[] {
    // HACK: Grouping by description even though you could have the same description and different product ids
    // Imagine that will be rare in an order though so for now it should do
    const orderMaterials = this.getPlainOrderMaterials()
    const quantized = orderMaterials.map((om) => ({
      ...om,
      quantity: om.quantity ? parseInt(om.quantity as unknown as string) : null,
    }))
    const grouped = groupBy(quantized, 'company_material.description')
    const data = values(grouped).map((group) => {
      const sum = sumBy(group, 'quantity')
      return { ...group[0], quantity: sum }
    })

    return filter(data, (om) => om.company_material.description) as OrderMaterial[]
  }

  @action
  async selectOrder(id?: string) {
    this.selectedOrderDirty = false
    this.newComment = ''

    if (!id) {
      this.selectedOrder = null
      this.orderMaterials = {}
      return
    }

    const { data } = await show(id)
    this.selectedOrder = data
    this.orderMaterials = groupBy(this.selectedOrder.order_materials, 'delivery_id')
    return data
  }

  async updateOrder(
    state_changes: OrderUpdateParams['state_changes'],
    vendor_files_signed_ids: string[],
    vendor_files_delete_ids: string[],
    quote_signed_id: string,
    vendor_user_ids: string[],
  ) {
    const { id, deliveries, state, quote_scheduled_to_expire_at } = this.selectedOrder
    const orderMaterials = this.getPlainOrderMaterials()

    const payload = {
      id,
      updated_at: this.selectedOrder?.updated_at,
      comment: this.newComment,
      state_changes,
      deliveries,
      order_materials: orderMaterials
        .map(resetCompanyMaterialId)
        .map(makeOrderMaterial)
        .map(formatOrderMaterialUnitCost),
      quote_signed_id,
      state,
      vendor_files_delete_ids,
      vendor_files_signed_ids,
      quote_scheduled_to_expire_at,
      vendor_user_ids,
    }
    await update(payload)
    this.selectOrder(id)
  }

  @action
  updateNewComment(newComment: string) {
    this.newComment = newComment
    this.selectedOrderDirty = true
  }

  materialsCount(deliveryId): number {
    return (this.getOrderMaterialsByDeliveryId(deliveryId) || []).length
  }

  deliveryTotalCost(delivery_id): number {
    const deliveryMaterials = this.getOrderMaterialsByDeliveryId(delivery_id) || []

    const subTotal = sumBy(deliveryMaterials, (orderMaterial) => {
      const unit = orderMaterial?.company_material?.unit

      return calcExtCost({
        unitCost: orderMaterial?.unit_cost as number,
        quantity: orderMaterial?.quantity,
        multiplier: unit?.multiplier as unknown as number,
        qtyIncrement: unit?.qty_increment as unknown as number,
      })
    })

    const delivery = this.selectedOrder?.deliveries.find((delivery) => delivery.id === delivery_id)

    const taxValue = Number(delivery?.tax_value || 0)
    const shippingValue = Number(delivery?.shipping_value || 0)
    const otherValue = Number(delivery?.other_value || 0)

    const discountValue = Number(delivery?.discount_value || 0)

    const extraValue = taxValue + shippingValue + otherValue

    return subTotal + extraValue - discountValue
  }

  @action
  async getOrderHistory(id: string) {
    const { data } = await history(id)

    const parsedHistory = data?.history.map((history) => ({
      ...history,
      title: history.title.replace('{{title_date}}', moment(new Date(history.title_date)).format('MMM D, h:mm A')),
    }))

    this.orderHistory.replace(parsedHistory)
  }

  @action
  setOrderDirty(isDirty = true) {
    this.selectedOrderDirty = isDirty
  }

  @action
  async updateOrderSession(
    orderId: string,
    user: OrderSessionOwner,
    payload?: UpdateOrderSessionBody,
  ): Promise<UpdateOrderSessionResponse> {
    try {
      await update_session({ id: orderId }, payload)
      this.orderSessionOwner = user
      return { ownerUser: user }
    } catch (error) {
      if (error?.response?.data.error === 'session_in_use_by_another_user') {
        this.orderSessionOwner = error?.response?.data.user
        return { ownerUser: error?.response?.data.user }
      }
      if (error?.response?.data.error === 'session_cannot_be_updated_by_impersonating_user') {
        this.orderSessionOwner = user
        return { ownerUser: user }
      }
      if (error?.response?.data.error === 'order_session_disabled_for_this_company') {
        this.orderSessionOwner = null
        return { ownerUser: null, disabled: true }
      }
      this.orderSessionOwner = null
      return null
    }
  }

  @action
  async releaseOrderSession(orderId: string): Promise<void> {
    try {
      await release_session({ id: orderId })
      this.orderSessionOwner = null
    } catch (error) {
      if (error?.response?.data.message === 'session_belongs_to_another_user') {
        this.orderSessionOwner = error?.response?.data.user
      }
      this.orderSessionOwner = null
    }
  }

  async sendComment(message: string) {
    await comment(this.selectedOrder.id, message)
    this.newComment = ''
    this.selectOrder(this.selectedOrder.id)
  }
}
